import { Component, inject, Input, OnInit, signal } from "@angular/core";
import { Subject, switchMap, takeUntil } from "rxjs";
import { DeviceServiceService } from "src/app/services/device-service/device-service.service";

@Component({
  selector: "app-banner-image",
  templateUrl: "./banner-image.component.html",
  styleUrl: "./banner-image.component.scss",
  standalone: false,
})
export class BannerImageComponent implements OnInit {
  @Input() bannerImageUrl: string =
    "./assets/media/media-cloned/landing/banner.jpg";
  @Input() bannerClasses: string = "";
  @Input() bannerOpacity: number = 0.6;
  @Input() bannerSize: "cover" | "contain" | "inherit" | "unset" = "cover";
  @Input() bannerPosition: "center" | "top" | "bottom" | "left" | "right" =
    "center";

  /**
   * How the photograph is quietened so the copy on top of it can be read.
   *
   * `light` is the original behaviour — a flat white veil at `bannerOpacity`,
   * which is right when the section's text is dark. It is the wrong tool for a
   * banner set in white: fading a photograph towards white until dark text
   * works leaves no photograph, and fading it only a little leaves no text.
   *
   * `ink` lays the palette's own dark over the picture instead, weighted to the
   * side the copy sits on, so the image stays a photograph at full strength on
   * the other side and the headline still measures over 12:1.
   */
  @Input() bannerScrim: "light" | "ink" | "ink-left" | "paper-left" = "light";

  #deviceDetection = inject(DeviceServiceService);

  /** The overlay laid over the photograph, per `bannerScrim`. */
  private get scrim(): string {
    switch (this.bannerScrim) {
      case "ink":
        return (
          "linear-gradient(180deg, rgba(6,34,49,0.55) 0%, rgba(6,34,49,0.78) 55%," +
          " rgba(6,34,49,0.92) 100%)"
        );
      case "paper-left":
        // For a light photograph whose subject sits to one side. A flat veil
        // would erase it — a near-white picture under a white wash is a white
        // bar — so this lifts only the left third, where the copy is, and has
        // let go entirely before it reaches the subject on the right.
        //
        // The weight over that third is measured, not guessed: the darkest
        // thing under the copy is a silver pen at 0.44 luminance, which leaves
        // 17px body text at 3.1:1. This brings the same pixels to 4.6:1 and
        // costs only the pen's contrast, which the picture can spare.
        return (
          "linear-gradient(90deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.64) 34%," +
          " rgba(255,255,255,0.42) 50%, rgba(255,255,255,0.12) 62%, rgba(255,255,255,0) 74%)"
        );
      case "ink-left":
        return (
          "linear-gradient(90deg, rgba(6,34,49,0.95) 0%, rgba(6,34,49,0.9) 32%," +
          " rgba(8,45,64,0.72) 55%, rgba(10,66,96,0.5) 78%, rgba(12,85,125,0.42) 100%)"
        );
      default: {
        const a = this.bannerOpacity;
        return `linear-gradient(rgba(255,255,255,${a}), rgba(255,255,255,${a}))`;
      }
    }
  }

  get bannerImage() {
    return {
      "background-image": `${this.scrim}, url(${this.bannerImageUrl})`,
      "background-position": `${this.bannerPosition}`,
      "background-size": `${this.bannerSize}`,
      "background-repeat": "no-repeat",
    };
  }
  destroy$ = new Subject<void>();
  isMobile = signal<boolean | null>(null);
  ngOnInit(): void {
    this.mobileDetector();
  }

  mobileDetector() {
    this.isMobile.update((_) => this.#deviceDetection.mobileDeviceListener());
    this.#deviceDetection
      .onResize()
      .pipe(
        switchMap(() => this.#deviceDetection.isMobile$),
        takeUntil(this.destroy$)
      )
      .subscribe((is_mobile: boolean) => {
        if (this.isMobile() != is_mobile) {
          this.isMobile.update((_) => is_mobile);
        }
      });
  }
}
